"use client";

import { useMemo, useState } from "react";
import { Button, Select as RACSelect, SelectValue, Tabs } from "react-aria-components";
import { useTranslations } from "next-intl";
import { clsx } from "clsx";

import IconChevronDownSmall from "@ui/central-icons/icons/IconChevronDownSmall";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { ListBox } from "@ui/components/core/ListBox";
import { TabList } from "@ui/components/core/TabList";
import {
  type PaygProductId,
  DEFAULT_PAYG_PRODUCT_ID,
  PAYG_PRODUCT_OPTIONS,
  getPaygProduct,
} from "../payg-pricing-data";
import { type CalculatorMode, usePricingTableContext } from "../../PricingTableContext";
import { BillingCycleToggle } from "../../BillingCycleToggle";
import { getStepLabel, getUnitShort, sliderToValue, valueToSlider } from "./calculatorUtils";
import { UsageSlider } from "./UsageSlider";
import { StepIndicator } from "./StepIndicator";
import { ApiCalculatorResultCard } from "./ApiCalculatorResultCard";

const TOGGLE_IDS = ["payg", "subscription"] as const satisfies readonly CalculatorMode[];

export interface ApiCalculatorProps {
  className?: string;
}

/**
 * API pricing calculator with PAYG / Subscription tabs.
 * Both tabs share the product + usage inputs — switching tabs preserves state.
 * Result card differs per tab: PAYG shows monthly $ cost, Subscription shows the
 * recommended tier covering the chosen usage.
 */
export function ApiCalculator({ className }: ApiCalculatorProps) {
  const t = useTranslations("PricingClientKeys.PaygCostCalculator");
  const { calculatorMode: mode, setCalculatorMode: setMode, billingCycle } = usePricingTableContext();

  const [{ productId, usage }, setState] = useState(() => ({
    productId: DEFAULT_PAYG_PRODUCT_ID,
    usage: getPaygProduct(DEFAULT_PAYG_PRODUCT_ID).defaultValue,
  }));

  const product = getPaygProduct(productId);
  const sliderValue = valueToSlider(usage, product.maxValue);

  const handleSliderChange = (next: number) =>
    setState(prev => ({ ...prev, usage: sliderToValue(next, product.maxValue) }));

  const handleProductChange = (id: PaygProductId) => {
    setState({ productId: id, usage: getPaygProduct(id).defaultValue });
  };

  const productItems = useMemo(
    () =>
      PAYG_PRODUCT_OPTIONS.map(p => ({
        id: p.id,
        textValue: t(`products.${p.id}` as Parameters<typeof t>[0]),
      })),
    [t],
  );

  const toggleItems = useMemo(
    () => TOGGLE_IDS.map(id => ({ id, title: t(`toggle_${id}` as Parameters<typeof t>[0]) })),
    [t],
  );

  const stepLabel = getStepLabel(t, product.unit);
  const unitShort = getUnitShort(t, product.unit);

  return (
    <div className={className}>
      <div className='tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 lg:tw-gap-16'>
        <div className='tw-w-full lg:tw-w-1/2 tw-space-y-8'>
          <div>
            <StepIndicator step={1} label={t("select_payment_model")} />

            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
              <Tabs selectedKey={mode} onSelectionChange={key => setMode(key as CalculatorMode)}>
                <TabList items={toggleItems} showRing size='md' />
              </Tabs>
              {mode === "subscription" && <BillingCycleToggle size='md' />}
            </div>
          </div>

          <div>
            <StepIndicator step={2} label={t("select_product")} />

            <RACSelect
              selectedKey={productId}
              onSelectionChange={key => {
                if (key !== null && typeof key === "string") {
                  handleProductChange(key as PaygProductId);
                }
              }}
              aria-label={t("select_product")}
            >
              <Button
                className={({ isFocusVisible }) =>
                  clsx(
                    "tw-w-full tw-text-base tw-font-normal tw-text-black tw-flex tw-items-center tw-justify-between tw-gap-2 tw-max-h-11 tw-px-4 tw-py-3 tw-rounded-xl tw-bg-white tw-border tw-border-gray-200 hover:tw-border-gray-300 tw-transition-colors tw-cursor-pointer",
                    isFocusVisible ? "tw-outline-focus" : "tw-outline-none focus:tw-border-black",
                  )
                }
              >
                <SelectValue className='tw-flex-1 tw-text-left' />
                <IconChevronDownSmall className={`tw-size-4 tw-text-gray-400 tw-flex-none ${iconStrokeFix}`} />
              </Button>
              <ListBox items={productItems} offset={8} />
            </RACSelect>
          </div>

          <div>
            <StepIndicator step={3} label={stepLabel} />

            <UsageSlider
              sliderValue={sliderValue}
              onSliderChange={handleSliderChange}
              maxValue={product.maxValue}
              unitSuffix={unitShort}
              ariaLabel={stepLabel}
            />
          </div>
        </div>

        <div className='tw-w-full lg:tw-w-1/2'>
          <ApiCalculatorResultCard mode={mode} product={product} usage={usage} period={billingCycle} />
        </div>
      </div>
    </div>
  );
}
